import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Play, Pause, ChevronLeft, ChevronRight } from 'lucide-react'
import * as Tone from 'tone'
import { AutomationEnvelope, AutomationPresets } from '@/utils/AutomationEnvelope'
import { AutomationEnvelopeOverlay } from '@/components/AutomationEnvelopeOverlay'
import { AutomationPresetLoader } from '@/utils/AutomationPresetLoader'
import { getApiBaseUrl } from '@/services/trackService'

// Import Multitrack
declare global {
  interface Window {
    Multitrack: any
  }
}

interface EQSettings {
  low: number
  mid: number
  high: number
}

export interface PlaygroundTransitionPlayerProps {
  prevtrackUrl: string
  nexttrackUrl: string
  trackPrevBpm: number
  trackNextStartBeat: number
  nbeats: number
  height?: number
  // Tone.js 效果器参数（可选）
  track1EQ?: EQSettings
  track2EQ?: EQSettings
  track1Filter?: number  // 0-100: 0-50 = lowpass, 50-100 = highpass, 50 = off
  track2Filter?: number
  track1Volume?: number  // dB
  track2Volume?: number  // dB
  onEQNodesReady?: (track1EQ: Tone.EQ3, track2EQ: Tone.EQ3) => void
  // Automation 预设（可选）
  volumeCurvePreset?: string
  eqCurvePreset?: string
  filterTypePreset?: string
  // Offset 初始值（可选）
  initialTrack1OffsetBeats?: number
  initialTrack2OffsetBeats?: number
  initialTransitionOffsetBeats?: number
  // Offset 改变回调（可选）
  onOffsetChange?: (track1Offset: number, track2Offset: number, transitionOffset: number) => void
  // Envelope 实时值回调（可选）- 用于 MIDI 输出
  onEnvelopeUpdate?: (values: {
    track1: {
      volume: number      // -38 to -6 dB
      filter: number      // 0-100
      eqLow: number      // -24 to 0 dB
      eqMid: number      // -24 to 0 dB
      eqHigh: number     // -24 to 0 dB
    }
    track2: {
      volume: number
      filter: number
      eqLow: number
      eqMid: number
      eqHigh: number
    }
  }) => void
}

// 音频效果链接口（导出供外部使用）
export interface AudioEffectChain {
  eq: Tone.EQ3
  filterLPF: Tone.Filter  // 低通滤波器
  filterHPF: Tone.Filter  // 高通滤波器
  volume: Tone.Volume
  input: Tone.ToneAudioNode
  output: Tone.ToneAudioNode
}

// 导出 EQ 设置接口
export type { EQSettings }

export const PlaygroundTransitionPlayer: React.FC<PlaygroundTransitionPlayerProps> = ({
  prevtrackUrl,
  nexttrackUrl,
  trackPrevBpm,
  trackNextStartBeat,
  nbeats,
  height = 120,
  track1EQ,
  track2EQ,
  track1Filter = 50,
  track2Filter = 50,
  track1Volume = 0,
  track2Volume = 0,
  onEQNodesReady,
  volumeCurvePreset,
  eqCurvePreset,
  filterTypePreset,
  initialTrack1OffsetBeats = 0,
  initialTrack2OffsetBeats = 0,
  initialTransitionOffsetBeats = 0,
  onOffsetChange,
  onEnvelopeUpdate,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const multitrackRef = useRef<any>(null)
  const canvasOverlayRef = useRef<HTMLCanvasElement>(null)
  
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [maxDuration, setMaxDuration] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [canPlay, setCanPlay] = useState(false)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  
  // 轨道偏移量状态（以拍为单位，整数倍）
  const [track1OffsetBeats, setTrack1OffsetBeats] = useState(initialTrack1OffsetBeats)
  const [track2OffsetBeats, setTrack2OffsetBeats] = useState(initialTrack2OffsetBeats)
  
  // 转场区域偏移量状态（以拍为单位，整数倍）
  const [transitionOffsetBeats, setTransitionOffsetBeats] = useState(initialTransitionOffsetBeats)
  
  // 静音状态
  const [muteState, setMuteState] = useState<'both' | 'track0' | 'track1'>('both')
  
  // 当offset改变时，通知父组件
  useEffect(() => {
    if (onOffsetChange) {
      onOffsetChange(track1OffsetBeats, track2OffsetBeats, transitionOffsetBeats)
    }
  }, [track1OffsetBeats, track2OffsetBeats, transitionOffsetBeats, onOffsetChange])
  
  // Tone.js 效果链节点
  // 每个轨道有完整的效果链：EQ -> Filter -> Volume -> Destination
  const track1EffectChainRef = useRef<AudioEffectChain | null>(null)
  const track2EffectChainRef = useRef<AudioEffectChain | null>(null)
  const isToneInitialized = useRef(false)
  const [eqActive, setEqActive] = useState(false)
  const audioRoutingAttempted = useRef(false)
  const [routingError, setRoutingError] = useState<string | null>(null)
  
  // 验证必需的props
  if (!prevtrackUrl || !nexttrackUrl || !trackPrevBpm) {
    console.error('❌ Missing required props:', { prevtrackUrl, nexttrackUrl, trackPrevBpm })
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
        <p className="text-red-600 dark:text-red-400 text-sm">
          缺少必要的播放器参数，无法加载播放器
        </p>
      </div>
    )
  }
  
  // 计算转场区域（需要在 state 初始化之前）
  // 应用转场区域偏移量
  const transitionStart = (32 + transitionOffsetBeats) * 60 / trackPrevBpm
  const transitionEnd = (32 + nbeats + transitionOffsetBeats) * 60 / trackPrevBpm
  
  // Automation Envelope 状态 - Filter
  const [track1FilterEnvelope, setTrack1FilterEnvelope] = useState<AutomationEnvelope>(() => 
    AutomationPresets.filterLowPassOut(transitionStart, transitionEnd)
  )
  const [track2FilterEnvelope, setTrack2FilterEnvelope] = useState<AutomationEnvelope>(() => 
    AutomationPresets.filterHighPassIn(transitionStart, transitionEnd)
  )
  
  // Automation Envelope 状态 - Volume
  const [track1VolumeEnvelope, setTrack1VolumeEnvelope] = useState<AutomationEnvelope>(() => 
    AutomationPresets.volumeFadeOut(transitionStart, transitionEnd)
  )
  const [track2VolumeEnvelope, setTrack2VolumeEnvelope] = useState<AutomationEnvelope>(() => 
    AutomationPresets.volumeFadeIn(transitionStart, transitionEnd)
  )
  
  // Automation Envelope 状态 - EQ (High, Mid, Low)
  const [track1EQLowEnvelope, setTrack1EQLowEnvelope] = useState<AutomationEnvelope>(() => 
    AutomationPresets.flat(transitionStart, transitionEnd)
  )
  const [track1EQMidEnvelope, setTrack1EQMidEnvelope] = useState<AutomationEnvelope>(() => 
    AutomationPresets.flat(transitionStart, transitionEnd)
  )
  const [track1EQHighEnvelope, setTrack1EQHighEnvelope] = useState<AutomationEnvelope>(() => 
    AutomationPresets.flat(transitionStart, transitionEnd)
  )
  const [track2EQLowEnvelope, setTrack2EQLowEnvelope] = useState<AutomationEnvelope>(() => 
    AutomationPresets.flat(transitionStart, transitionEnd)
  )
  const [track2EQMidEnvelope, setTrack2EQMidEnvelope] = useState<AutomationEnvelope>(() => 
    AutomationPresets.flat(transitionStart, transitionEnd)
  )
  const [track2EQHighEnvelope, setTrack2EQHighEnvelope] = useState<AutomationEnvelope>(() => 
    AutomationPresets.flat(transitionStart, transitionEnd)
  )
  
  const animationFrameRef = useRef<number>(0)
  
  // 使用 ref 保存上一次的 nbeats 值，用于检测变化
  const prevNbeatsRef = useRef(nbeats)
  
  // 性能优化：节流状态更新的时间戳
  const lastStateUpdateTime = useRef<number>(0)
  const STATE_UPDATE_INTERVAL = 1000 / 30 // 30Hz 更新频率，降低 React 渲染压力
  
  // 性能优化：使用 ref 存储 envelope 对象，避免 useEffect 依赖数组过大导致频繁重建动画循环
  const track1FilterEnvelopeRef = useRef(track1FilterEnvelope)
  const track2FilterEnvelopeRef = useRef(track2FilterEnvelope)
  const track1VolumeEnvelopeRef = useRef(track1VolumeEnvelope)
  const track2VolumeEnvelopeRef = useRef(track2VolumeEnvelope)
  const track1EQLowEnvelopeRef = useRef(track1EQLowEnvelope)
  const track1EQMidEnvelopeRef = useRef(track1EQMidEnvelope)
  const track1EQHighEnvelopeRef = useRef(track1EQHighEnvelope)
  const track2EQLowEnvelopeRef = useRef(track2EQLowEnvelope)
  const track2EQMidEnvelopeRef = useRef(track2EQMidEnvelope)
  const track2EQHighEnvelopeRef = useRef(track2EQHighEnvelope)
  
  // 当 envelope state 改变时，更新对应的 ref
  useEffect(() => {
    track1FilterEnvelopeRef.current = track1FilterEnvelope
  }, [track1FilterEnvelope])
  
  useEffect(() => {
    track2FilterEnvelopeRef.current = track2FilterEnvelope
  }, [track2FilterEnvelope])
  
  useEffect(() => {
    track1VolumeEnvelopeRef.current = track1VolumeEnvelope
  }, [track1VolumeEnvelope])
  
  useEffect(() => {
    track2VolumeEnvelopeRef.current = track2VolumeEnvelope
  }, [track2VolumeEnvelope])
  
  useEffect(() => {
    track1EQLowEnvelopeRef.current = track1EQLowEnvelope
    track1EQMidEnvelopeRef.current = track1EQMidEnvelope
    track1EQHighEnvelopeRef.current = track1EQHighEnvelope
  }, [track1EQLowEnvelope, track1EQMidEnvelope, track1EQHighEnvelope])
  
  useEffect(() => {
    track2EQLowEnvelopeRef.current = track2EQLowEnvelope
    track2EQMidEnvelopeRef.current = track2EQMidEnvelope
    track2EQHighEnvelopeRef.current = track2EQHighEnvelope
  }, [track2EQLowEnvelope, track2EQMidEnvelope, track2EQHighEnvelope])
  
  // 实时 Filter 和 Volume 值监控
  // 【完整效果链已激活：EQ + Filter + Volume】
  const [realtimeTrack1Filter, setRealtimeTrack1Filter] = useState(50)
  const [realtimeTrack2Filter, setRealtimeTrack2Filter] = useState(50)
  const [realtimeTrack1FilterFreq, setRealtimeTrack1FilterFreq] = useState(0)
  const [realtimeTrack2FilterFreq, setRealtimeTrack2FilterFreq] = useState(0)
  
  // 实时 Volume 值监控（dB）
  const [realtimeTrack1Volume, setRealtimeTrack1Volume] = useState(0)
  const [realtimeTrack2Volume, setRealtimeTrack2Volume] = useState(0)
  
  // 实时 EQ 值监控（dB）
  const [realtimeTrack1EQLow, setRealtimeTrack1EQLow] = useState(0)
  const [realtimeTrack1EQMid, setRealtimeTrack1EQMid] = useState(0)
  const [realtimeTrack1EQHigh, setRealtimeTrack1EQHigh] = useState(0)
  const [realtimeTrack2EQLow, setRealtimeTrack2EQLow] = useState(0)
  const [realtimeTrack2EQMid, setRealtimeTrack2EQMid] = useState(0)
  const [realtimeTrack2EQHigh, setRealtimeTrack2EQHigh] = useState(0)
  
  // 当预设改变时，自动加载对应的 envelope
  useEffect(() => {
    if (volumeCurvePreset) {
      const volumeEnvelopes = AutomationPresetLoader.loadVolumeCurve(
        volumeCurvePreset,
        transitionStart,
        transitionEnd
      )
      if (volumeEnvelopes) {
        setTrack1VolumeEnvelope(volumeEnvelopes.track1Volume)
        setTrack2VolumeEnvelope(volumeEnvelopes.track2Volume)
        console.log('✅ Loaded volume curve preset:', volumeCurvePreset)
      }
    }
  }, [volumeCurvePreset, transitionStart, transitionEnd])
  
  useEffect(() => {
    if (eqCurvePreset) {
      const eqEnvelopes = AutomationPresetLoader.loadEQCurve(
        eqCurvePreset,
        transitionStart,
        transitionEnd
      )
      if (eqEnvelopes) {
        setTrack1EQLowEnvelope(eqEnvelopes.track1EQLow)
        setTrack1EQMidEnvelope(eqEnvelopes.track1EQMid)
        setTrack1EQHighEnvelope(eqEnvelopes.track1EQHigh)
        setTrack2EQLowEnvelope(eqEnvelopes.track2EQLow)
        setTrack2EQMidEnvelope(eqEnvelopes.track2EQMid)
        setTrack2EQHighEnvelope(eqEnvelopes.track2EQHigh)
        console.log('✅ Loaded EQ curve preset:', eqCurvePreset)
      }
    }
  }, [eqCurvePreset, transitionStart, transitionEnd])
  
  useEffect(() => {
    if (filterTypePreset) {
      const filterEnvelopes = AutomationPresetLoader.loadFilterType(
        filterTypePreset,
        transitionStart,
        transitionEnd
      )
      if (filterEnvelopes) {
        setTrack1FilterEnvelope(filterEnvelopes.track1Filter)
        setTrack2FilterEnvelope(filterEnvelopes.track2Filter)
        console.log('✅ Loaded filter type preset:', filterTypePreset)
      }
    }
  }, [filterTypePreset, transitionStart, transitionEnd])
  
  // 计算 nexttrack 的起始位置（在timeline上的插入位置）
  const nexttrackStartPosition = (32 - trackNextStartBeat) * 60 / trackPrevBpm
  
  // 计算 nexttrack 音频文件内部的起始偏移（音频文件从哪一拍开始）
  const nexttrackAudioOffset = trackNextStartBeat * 60 / trackPrevBpm
  
  // console.log('🎯 Track2 Time Calculation:', {
  //   nexttrackStartPosition,
  //   nexttrackAudioOffset,
  //   trackNextStartBeat,
  //   trackPrevBpm,
  //   formula: `track2Time = currentTime - ${nexttrackStartPosition.toFixed(2)} + ${nexttrackAudioOffset.toFixed(2)}`
  // })
  
  // 设置DOM拖动监听器（备选方案）
  const setupDragListeners = useCallback(() => {
    if (!multitrackRef.current || !containerRef.current) {
      console.warn('⚠️ Cannot setup drag listeners: missing refs')
      return
    }
    
    console.log('🎯 Setting up DOM drag listeners...')
    
    const tracks = multitrackRef.current.tracks
    const wavesurfers = multitrackRef.current.wavesurfers
    
    if (!tracks || !wavesurfers || tracks.length < 2 || wavesurfers.length < 2) {
      console.warn('⚠️ Cannot setup drag listeners: missing tracks or wavesurfers')
      return
    }
    
    // 存储每个轨道的上一次位置（使用 ref 避免闭包问题）
    const lastPositions: { [key: number]: number } = {}
    
    // 为每个轨道设置监听器
    tracks.forEach((track: any, trackId: number) => {
      const ws = wavesurfers[trackId]
      if (!ws) return
      
      // 初始化上一次位置
      lastPositions[trackId] = track.startPosition || 0
      
      // 查找波形的容器元素 - 尝试多种方式
      let container = ws.container || ws.drawer?.container || ws.wrapper
      
      // 如果还找不到，尝试通过 DOM 查询
      if (!container) {
        const allContainers = containerRef.current?.querySelectorAll('[data-id]')
        if (allContainers && allContainers[trackId]) {
          container = allContainers[trackId] as HTMLElement
        }
      }
      
      // 最后尝试：查找所有可拖动的元素
      if (!container) {
        const allWrappers = containerRef.current?.querySelectorAll('div[style*="cursor"]')
        if (allWrappers && allWrappers[trackId]) {
          container = allWrappers[trackId] as HTMLElement
        }
      }
      
      if (!container) {
        console.warn(`⚠️ No container found for track ${trackId}`)
        // 尝试直接监听 track 对象的变化
        console.log(`🔍 Track ${trackId} object:`, track)
        console.log(`🔍 WaveSurfer ${trackId} object:`, ws)
        return
      }
      
      console.log(`✅ Found container for track ${trackId}:`, container)
      
      // 使用 MutationObserver 监听容器的 transform 或 left 样式变化
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            // 读取当前的 left 值
            const containerElement = mutation.target as HTMLElement
            const leftStyle = containerElement.style.left
            const transformStyle = containerElement.style.transform
            
            let currentStartPosition = track.startPosition || 0
            let positionUpdated = false
            
            // 尝试从 style 中解析位置
            if (leftStyle && leftStyle !== '0px') {
              const leftPx = parseFloat(leftStyle)
              if (!isNaN(leftPx) && containerRef.current) {
                const duration = maxDuration
                const containerWidth = containerRef.current.clientWidth
                if (duration > 0 && containerWidth > 0) {
                  const pixelsPerSecond = containerWidth / duration
                  currentStartPosition = leftPx / pixelsPerSecond
                  positionUpdated = true
                }
              }
            }
            
            // 如果 left 没有值，尝试从 transform 解析
            if (!positionUpdated && transformStyle && transformStyle.includes('translateX')) {
              const match = transformStyle.match(/translateX\(([^)]+)\)/)
              if (match) {
                const translateX = parseFloat(match[1])
                if (!isNaN(translateX) && containerRef.current) {
                  const duration = maxDuration
                  const containerWidth = containerRef.current.clientWidth
                  if (duration > 0 && containerWidth > 0) {
                    const pixelsPerSecond = containerWidth / duration
                    currentStartPosition = translateX / pixelsPerSecond
                    positionUpdated = true
                  }
                }
              }
            }
            
            // 只有成功解析到位置时才更新
            if (positionUpdated) {
              // 更新 track.startPosition（重要：同步回对象）
              track.startPosition = currentStartPosition
              
              // 计算偏移量
              const offsetSeconds = trackId === 0 
                ? currentStartPosition 
                : currentStartPosition - nexttrackStartPosition
              
              const beatDuration = 60 / trackPrevBpm
              const offsetBeats = offsetSeconds / beatDuration
              
              // 更新状态
              if (trackId === 0) {
                setTrack1OffsetBeats(offsetBeats)
              } else if (trackId === 1) {
                setTrack2OffsetBeats(offsetBeats)
              }
              
              // 偶尔打印日志（避免刷屏）
              if (Math.random() < 0.1) {
                console.log(`🎯 Track ${trackId} position changed:`, {
                  startPosition: currentStartPosition.toFixed(3),
                  offsetBeats: offsetBeats.toFixed(2),
                  source: leftStyle && leftStyle !== '0px' ? 'left' : 'transform'
                })
              }
            }
          }
        })
      })
      
      // 监听 style 和其他属性变化
      observer.observe(container, {
        attributes: true,
        attributeFilter: ['style', 'data-start-position']
      })
      
      // 监听 mouseup 事件（拖动结束） - 使用全局监听
      const handleMouseUp = () => {
        // 延迟检查，确保位置已更新
        setTimeout(() => {
          const currentStartPosition = track.startPosition || 0
          const lastPos = lastPositions[trackId] || 0
          
          // 检查位置是否改变
          if (Math.abs(currentStartPosition - lastPos) > 0.01) {
            console.log(`🎯 Track ${trackId} drag ended, applying snap...`, {
              from: lastPos.toFixed(3),
              to: currentStartPosition.toFixed(3)
            })
            
            // 计算偏移量
            const offsetSeconds = trackId === 0 
              ? currentStartPosition 
              : currentStartPosition - nexttrackStartPosition
            
            const beatDuration = 60 / trackPrevBpm
            const offsetBeats = offsetSeconds / beatDuration
            
            // 吸附到最近的整数拍
            const snappedBeats = Math.round(offsetBeats)
            const snappedSeconds = snappedBeats * beatDuration
            
            const snappedStartPosition = trackId === 0 
              ? snappedSeconds 
              : nexttrackStartPosition + snappedSeconds
            
            // 检查是否需要吸附
            const needsSnap = Math.abs(currentStartPosition - snappedStartPosition) > 0.01
            
            if (needsSnap) {
              // 更新轨道位置
              track.startPosition = snappedStartPosition
              
              // 强制重新渲染
              const duration = maxDuration || ws.getDuration?.() || 0
              const containerWidth = containerRef.current?.clientWidth || 0
              
              if (duration > 0 && containerWidth > 0) {
                const pixelsPerSecond = containerWidth / duration
                const leftPx = snappedStartPosition * pixelsPerSecond
                
                console.log(`🔧 Applying snap to track ${trackId}:`, {
                  snappedStartPosition: snappedStartPosition.toFixed(3),
                  leftPx: leftPx.toFixed(2),
                  currentLeft: container.style.left,
                  currentTransform: container.style.transform,
                  hasLeft: !!container.style.left,
                  hasTransform: !!container.style.transform
                })
                
                // 优先尝试更新 left 样式（检查是否已存在）
                const hasLeftStyle = container.style.left && container.style.left !== '0px'
                const hasTransformStyle = container.style.transform && container.style.transform.includes('translateX')
                
                if (hasLeftStyle || (!hasLeftStyle && !hasTransformStyle)) {
                  // 使用 left 样式
                  container.style.left = `${leftPx}px`
                  console.log(`✅ Updated track ${trackId} using left style: ${leftPx}px`)
                } else if (hasTransformStyle) {
                  // 使用 transform 样式
                  const otherTransforms = container.style.transform.replace(/translateX\([^)]+\)/, '').trim()
                  const newTransform = otherTransforms 
                    ? `translateX(${leftPx}px) ${otherTransforms}`
                    : `translateX(${leftPx}px)`
                  container.style.transform = newTransform
                  console.log(`✅ Updated track ${trackId} using transform: ${newTransform}`)
                }
                
                // 触发重排以强制更新
                void container.offsetHeight
              }
              
              console.log(`✨ Track ${trackId} snapped to ${snappedBeats} beats (${snappedStartPosition.toFixed(3)}s)`)
            }
            
            // 更新状态到吸附值
            if (trackId === 0) {
              setTrack1OffsetBeats(snappedBeats)
            } else if (trackId === 1) {
              setTrack2OffsetBeats(snappedBeats)
            }
            
            lastPositions[trackId] = snappedStartPosition
          }
        }, 50) // 延迟50ms确保位置已更新
      }
      
      document.addEventListener('mouseup', handleMouseUp)
      
      console.log(`✅ Drag listeners setup for track ${trackId}`)
    })
  }, [maxDuration, nexttrackStartPosition, trackPrevBpm])
  
  // 音频路由连接函数
  const connectAudioRouting = useCallback(() => {
    if (!multitrackRef.current || !track1EffectChainRef.current || !track2EffectChainRef.current) {
      setRoutingError('效果链或播放器未初始化')
      return false
    }
    
    try {
      console.log('🔌 Attempting audio routing connection...')
      console.log('📦 Multitrack ref:', multitrackRef.current)
      
      const tracks = multitrackRef.current.tracks
      console.log('🎵 Tracks array:', tracks)
      
      if (!tracks || tracks.length < 2) {
        setRoutingError(`轨道数量不足 (${tracks?.length || 0}/2)`)
        console.error('❌ Not enough tracks:', tracks)
        return false
      }
      
      console.log('🎵 Track 0 structure:', tracks[0])
      console.log('🎵 Track 1 structure:', tracks[1])
      
      // 正确的方式：从 multitrack.wavesurfers 数组获取
      const wavesurfers = multitrackRef.current.wavesurfers
      console.log('🎵 Wavesurfers array:', wavesurfers)
      
      if (!wavesurfers || wavesurfers.length < 2) {
        setRoutingError(`WaveSurfer 实例不足 (${wavesurfers?.length || 0}/2)`)
        console.error('❌ Not enough wavesurfer instances:', wavesurfers)
        return false
      }
      
      const track1WS = wavesurfers[0]
      const track2WS = wavesurfers[1]
      
      console.log('🎵 WaveSurfer instances:', { track1WS, track2WS })
      console.log('🎵 Track 1 WS keys:', track1WS ? Object.keys(track1WS).slice(0, 30) : 'null')
      console.log('🎵 Track 2 WS keys:', track2WS ? Object.keys(track2WS).slice(0, 30) : 'null')
      console.log('🎵 Track 1 WS type:', track1WS?.constructor?.name)
      console.log('🎵 Track 2 WS type:', track2WS?.constructor?.name)
      
      if (!track1WS || !track2WS) {
        setRoutingError('无法访问 WaveSurfer 实例')
        console.error('❌ No WaveSurfer instances found')
        return false
      }
      
      // 获取 media 元素 - WaveSurfer 7.x 使用 getMediaElement()
      const track1Media = track1WS.getMediaElement?.() || track1WS.media || track1WS.backend?.media
      const track2Media = track2WS.getMediaElement?.() || track2WS.media || track2WS.backend?.media
      
      console.log('🎬 Media elements:', { track1Media, track2Media })
      
      if (!track1Media || !track2Media) {
        setRoutingError('无法获取音频元素')
        return false
      }
      
      // 获取 Tone.js AudioContext
      const toneContext = Tone.getContext()
      const nativeContext = toneContext.rawContext as AudioContext
      
      let track1Source: AudioNode | null = null
      let track2Source: AudioNode | null = null
      
      // 尝试获取或创建音频源节点
      try {
        track1Source = nativeContext.createMediaElementSource(track1Media)
        console.log('✅ Created MediaElementSource for track 1')
      } catch (error) {
        console.warn('Track 1 source already exists, trying alternative...')
        // 尝试查找已存在的节点
        if ((track1WS as any).backend?.sourceNode) {
          track1Source = (track1WS as any).backend.sourceNode
        } else if ((track1WS as any).backend?.gainNode) {
          track1Source = (track1WS as any).backend.gainNode
        }
      }
      
      try {
        track2Source = nativeContext.createMediaElementSource(track2Media)
        console.log('✅ Created MediaElementSource for track 2')
      } catch (error) {
        console.warn('Track 2 source already exists, trying alternative...')
        if ((track2WS as any).backend?.sourceNode) {
          track2Source = (track2WS as any).backend.sourceNode
        } else if ((track2WS as any).backend?.gainNode) {
          track2Source = (track2WS as any).backend.gainNode
        }
      }
      
      if (!track1Source || !track2Source) {
        setRoutingError('无法创建音频源节点')
        return false
      }
      
      // 断开现有连接
      try {
        track1Source.disconnect()
        track2Source.disconnect()
      } catch (e) {
        // 忽略 - 可能没有现有连接
      }
      
      // 连接到 Tone.js 效果链
      // 需要获取 Tone.js 节点的原生 Web Audio API 输入节点
      // 首先确保两个 AudioContext 是同一个
      const track1SourceContext = (track1Source as any).context
      const track2SourceContext = (track2Source as any).context
      
      console.log('🎛️ Audio contexts:', {
        track1SourceContext,
        track2SourceContext,
        toneContext: toneContext.rawContext,
        areSame: track1SourceContext === toneContext.rawContext
      })
      
      // 如果不是同一个 context，需要特殊处理
      if (track1SourceContext !== toneContext.rawContext) {
        console.warn('⚠️ Different AudioContexts detected - this may cause issues')
      }
      
      // 获取 Tone.js 节点的原生输入
      // 注意：EQ3 节点的 input 需要特殊处理
      const track1ToneInput = track1EffectChainRef.current.input
      const track2ToneInput = track2EffectChainRef.current.input
      
      console.log('🔌 Tone.js input nodes:', {
        track1ToneInput,
        track2ToneInput,
        track1Type: track1ToneInput?.constructor?.name,
        track2Type: track2ToneInput?.constructor?.name
      })
      
      // 方法1: 尝试使用 Tone.js 的 context.createGain() 作为桥接
      // 创建一个 native gain 节点作为中间节点
      const track1Bridge = nativeContext.createGain()
      const track2Bridge = nativeContext.createGain()
      
      console.log('🌉 Created bridge gain nodes')
      
      // 连接 source -> bridge
      track1Source.connect(track1Bridge)
      track2Source.connect(track2Bridge)
      
      // 使用 Tone.js 的 context.connect 将 native 节点连接到 Tone 节点
      // 需要使用 Tone.connect() 函数
      try {
        // 将 native 节点转换为 Tone.js 可以理解的格式
        Tone.connect(track1Bridge, track1ToneInput)
        Tone.connect(track2Bridge, track2ToneInput)
        
        console.log('✅ Connected using Tone.connect()')
      } catch (connectError) {
        console.error('❌ Tone.connect failed:', connectError)
        
        // 备用方法：直接访问内部的 _nativeAudioNode 或 input.input
        // EQ3 节点的结构：EQ3 -> _MultibandSplit -> input -> GainNode
        const track1NativeInput = (track1ToneInput as any).input?.input?._nativeAudioNode ||
                                   (track1ToneInput as any).input?.input ||
                                   (track1ToneInput as any).input?._nativeAudioNode ||
                                   (track1ToneInput as any)._nativeAudioNode
        const track2NativeInput = (track2ToneInput as any).input?.input?._nativeAudioNode ||
                                   (track2ToneInput as any).input?.input ||
                                   (track2ToneInput as any).input?._nativeAudioNode ||
                                   (track2ToneInput as any)._nativeAudioNode
        
        console.log('🔌 Native input nodes:', {
          track1NativeInput,
          track2NativeInput,
          track1Type: track1NativeInput?.constructor?.name,
          track2Type: track2NativeInput?.constructor?.name,
          track1ToneStructure: {
            hasInput: !!(track1ToneInput as any).input,
            hasInputInput: !!(track1ToneInput as any).input?.input,
            hasNative: !!(track1ToneInput as any)._nativeAudioNode
          }
        })
        
        if (track1NativeInput && track2NativeInput) {
          track1Bridge.connect(track1NativeInput)
          track2Bridge.connect(track2NativeInput)
          console.log('✅ Connected using native nodes directly')
        } else {
          throw new Error('无法找到原生音频输入节点')
        }
      }
      
      setEqActive(true)
      setRoutingError(null)
      console.log('✅ Audio routing successfully connected!')
      return true
      
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : '未知错误'
      setRoutingError(errorMsg)
      console.error('❌ Audio routing error:', error)
      return false
    }
  }, [])
  
  // console.log('🎯 Playground Transition Setup:', {
  //   prevtrackUrl,
  //   nexttrackUrl,
  //   trackPrevBpm,
  //   trackNextStartBeat,
  //   nbeats,
  //   transitionStart,
  //   transitionEnd,
  //   nexttrackStartPosition
  // })
  
  // 初始化 Tone.js 效果链
  useEffect(() => {
    const initToneEffects = async () => {
      if (isToneInitialized.current) return
      
      try {
        // 确保 Tone.js 音频上下文已启动
        await Tone.start()
        console.log('🎵 Tone.js Audio Context started for Playground Player')
        
        // 创建轨道1的效果链：EQ -> [LPF + HPF 并联] -> Volume -> Destination
        // 【双滤波器架构：避免类型切换的停顿】
        const track1EQNode = new Tone.EQ3({
          low: track1EQ?.low || 0,
          mid: track1EQ?.mid || 0,
          high: track1EQ?.high || 0,
          lowFrequency: 400,
          highFrequency: 2500,
        })
        
        // 低通滤波器 - 默认完全放开（20000Hz）
        const track1FilterLPF = new Tone.Filter({
          type: 'lowpass',
          frequency: 20000,
          rolloff: -24,
          Q: 8,  // 提高resonance，从3提升到8
        })
        
        // 高通滤波器 - 默认完全放开（20Hz）
        const track1FilterHPF = new Tone.Filter({
          type: 'highpass',
          frequency: 20,
          rolloff: -24,
          Q: 8,  // 提高resonance，从3提升到8
        })
        
        const track1VolumeNode = new Tone.Volume(-6) // 默认 -6dB 补偿双滤波器并联的增益
        
        // 连接效果链：EQ -> [LPF + HPF 并联] -> Volume -> Destination
        // 两个滤波器并联工作，同时处理信号
        track1EQNode.connect(track1FilterLPF)
        track1EQNode.connect(track1FilterHPF)
        track1FilterLPF.connect(track1VolumeNode)
        track1FilterHPF.connect(track1VolumeNode)
        track1VolumeNode.toDestination()
        
        track1EffectChainRef.current = {
          eq: track1EQNode,
          filterLPF: track1FilterLPF,
          filterHPF: track1FilterHPF,
          volume: track1VolumeNode,
          input: track1EQNode,  // 输入到EQ节点
          output: track1VolumeNode,
        }
        
        // 创建轨道2的效果链：EQ -> [LPF + HPF 并联] -> Volume -> Destination
        // 【双滤波器架构：避免类型切换的停顿】
        const track2EQNode = new Tone.EQ3({
          low: track2EQ?.low || 0,
          mid: track2EQ?.mid || 0,
          high: track2EQ?.high || 0,
          lowFrequency: 400,
          highFrequency: 2500,
        })
        
        // 低通滤波器 - 默认完全放开（20000Hz）
        const track2FilterLPF = new Tone.Filter({
          type: 'lowpass',
          frequency: 20000,
          rolloff: -24,
          Q: 8,  // 提高resonance，从3提升到8
        })
        
        // 高通滤波器 - 默认完全放开（20Hz）
        const track2FilterHPF = new Tone.Filter({
          type: 'highpass',
          frequency: 20,
          rolloff: -24,
          Q: 8,  // 提高resonance，从3提升到8
        })
        
        const track2VolumeNode = new Tone.Volume(-6) // 默认 -6dB 补偿双滤波器并联的增益
        
        // 连接效果链：EQ -> [LPF + HPF 并联] -> Volume -> Destination
        track2EQNode.connect(track2FilterLPF)
        track2EQNode.connect(track2FilterHPF)
        track2FilterLPF.connect(track2VolumeNode)
        track2FilterHPF.connect(track2VolumeNode)
        track2VolumeNode.toDestination()
        
        track2EffectChainRef.current = {
          eq: track2EQNode,
          filterLPF: track2FilterLPF,
          filterHPF: track2FilterHPF,
          volume: track2VolumeNode,
          input: track2EQNode,  // 输入到EQ节点
          output: track2VolumeNode,
        }
        
        console.log('✅ Tone.js effect chains initialized (Dual Filter Architecture: EQ → [LPF+HPF] → Volume):', {
          track1: track1EffectChainRef.current,
          track2: track2EffectChainRef.current
        })
        isToneInitialized.current = true
        
        // 通知父组件 EQ 节点已准备好
        if (onEQNodesReady) {
          onEQNodesReady(track1EQNode, track2EQNode)
        }
      } catch (error) {
        console.error('❌ Error initializing Tone.js effects:', error)
      }
    }
    
    initToneEffects()
    
    // 清理函数
    return () => {
      if (track1EffectChainRef.current) {
        track1EffectChainRef.current.eq.dispose()
        track1EffectChainRef.current.filterLPF.dispose()
        track1EffectChainRef.current.filterHPF.dispose()
        track1EffectChainRef.current.volume.dispose()
        track1EffectChainRef.current = null
      }
      if (track2EffectChainRef.current) {
        track2EffectChainRef.current.eq.dispose()
        track2EffectChainRef.current.filterLPF.dispose()
        track2EffectChainRef.current.filterHPF.dispose()
        track2EffectChainRef.current.volume.dispose()
        track2EffectChainRef.current = null
      }
      isToneInitialized.current = false
    }
  }, [])
  
  // 更新轨道1的EQ参数
  useEffect(() => {
    if (track1EffectChainRef.current && track1EQ) {
      track1EffectChainRef.current.eq.low.value = track1EQ.low
      track1EffectChainRef.current.eq.mid.value = track1EQ.mid
      track1EffectChainRef.current.eq.high.value = track1EQ.high
      console.log('🎚️ Track 1 EQ updated:', track1EQ)
    }
  }, [track1EQ])
  
  // 更新轨道2的EQ参数
  useEffect(() => {
    if (track2EffectChainRef.current && track2EQ) {
      track2EffectChainRef.current.eq.low.value = track2EQ.low
      track2EffectChainRef.current.eq.mid.value = track2EQ.mid
      track2EffectChainRef.current.eq.high.value = track2EQ.high
      console.log('🎚️ Track 2 EQ updated:', track2EQ)
    }
  }, [track2EQ])
  
  /**
   * 计算 Filter 截止频率
   * 使用对数映射，输入 0-1 映射到 20Hz-20000Hz，更符合听感
   * 
   * @param cutoffScale 归一化的 cutoff 值，范围 0-1
   * @returns 截止频率 (Hz)
   */
  const calculateCutoffFrequency = (cutoffScale: number): number => {
    // 确保输入范围在 0-1 之间
    cutoffScale = Math.max(0, Math.min(1, cutoffScale))
    
    // 使用对数映射，从20Hz到20000Hz
    // 20Hz = 1.3, 20000Hz = 4.3 (以10为底的对数)
    const minFreq = Math.log10(20)    // 约1.3
    const maxFreq = Math.log10(20000) // 约4.3
    
    // 对数插值
    const logFreq = minFreq + cutoffScale * (maxFreq - minFreq)
    const cutoffHz = Math.pow(10, logFreq)
    
    return Math.round(cutoffHz)
  }
  
  // 更新轨道1的Filter参数（双滤波器架构）
  // 参数范围 0-100: 0-50=LPF响应, 50=OFF(两个都放开), 50-100=HPF响应
  useEffect(() => {
    if (track1EffectChainRef.current) {
      const { filterLPF, filterHPF } = track1EffectChainRef.current
      
      if (track1Filter === 50) {
        // 50 = OFF, 两个滤波器都完全放开
        filterLPF.frequency.value = 20000  // LPF 放开（通过所有高频）
        filterHPF.frequency.value = 20     // HPF 放开（通过所有低频）
      } else if (track1Filter < 50) {
        // 0-49 = Low Pass Filter 响应
        // LPF 截止频率降低，HPF 保持放开
        const normalized = track1Filter / 50  // 0-1
        const cutoffHz = calculateCutoffFrequency(normalized)
        filterLPF.frequency.value = Math.max(20, cutoffHz)
        filterHPF.frequency.value = 20  // HPF 保持放开
      } else {
        // 51-100 = High Pass Filter 响应
        // HPF 截止频率升高，LPF 保持放开
        const normalized = (track1Filter - 50) / 50  // 0-1
        const cutoffHz = calculateCutoffFrequency(normalized)
        filterLPF.frequency.value = 20000  // LPF 保持放开
        filterHPF.frequency.value = Math.max(20, cutoffHz)
      }
      
      console.log('🎛️ Track 1 Dual Filter updated:', {
        value: track1Filter,
        lpfFreq: filterLPF.frequency.value,
        hpfFreq: filterHPF.frequency.value
      })
    }
  }, [track1Filter])
  
  // 更新轨道2的Filter参数（双滤波器架构）
  useEffect(() => {
    if (track2EffectChainRef.current) {
      const { filterLPF, filterHPF } = track2EffectChainRef.current
      
      if (track2Filter === 50) {
        // 50 = OFF, 两个滤波器都完全放开
        filterLPF.frequency.value = 20000
        filterHPF.frequency.value = 20
      } else if (track2Filter < 50) {
        // 0-49 = Low Pass Filter 响应
        const normalized = track2Filter / 50
        const cutoffHz = calculateCutoffFrequency(normalized)
        filterLPF.frequency.value = Math.max(20, cutoffHz)
        filterHPF.frequency.value = 20
      } else {
        // 51-100 = High Pass Filter 响应
        const normalized = (track2Filter - 50) / 50
        const cutoffHz = calculateCutoffFrequency(normalized)
        filterLPF.frequency.value = 20000
        filterHPF.frequency.value = Math.max(20, cutoffHz)
      }
      
      console.log('🎛️ Track 2 Dual Filter updated:', {
        value: track2Filter,
        lpfFreq: filterLPF.frequency.value,
        hpfFreq: filterHPF.frequency.value
      })
    }
  }, [track2Filter])
  
  // 更新轨道1的Volume参数（需要减去6dB补偿）
  useEffect(() => {
    if (track1EffectChainRef.current) {
      track1EffectChainRef.current.volume.volume.value = track1Volume - 6
      console.log('🔊 Track 1 Volume updated:', track1Volume - 6)
    }
  }, [track1Volume])
  
  // 更新轨道2的Volume参数（需要减去6dB补偿）
  useEffect(() => {
    if (track2EffectChainRef.current) {
      track2EffectChainRef.current.volume.volume.value = track2Volume - 6
      console.log('🔊 Track 2 Volume updated:', track2Volume - 6)
    }
  }, [track2Volume])
  
  // 实时更新 Automation（Filter, Volume, EQ）基于当前播放时间
  useEffect(() => {
    const automationEnabled = true // Automation 始终启用
    if (!automationEnabled || !isPlaying) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = 0
      }
      return
    }
    
    const updateAutomation = () => {
      if (!multitrackRef.current || !isPlaying) {
        return
      }
      
      const currentTime = multitrackRef.current.getCurrentTime()
      const now = performance.now()
      
      // 检查是否播放到结尾附近（距离结尾 0.1 秒内）
      if (maxDuration > 0 && currentTime >= maxDuration - 0.1) {
        console.log('⏹️ Reached end of playback, resetting...')
        
        // 停止播放
        multitrackRef.current.pause()
        setIsPlaying(false)
        
        // 计算初始 playhead 位置（transitionStart - 8拍）
        const eightBeatsTime = 8 * 60 / trackPrevBpm
        const initialPlayheadPosition = Math.max(0, transitionStart - eightBeatsTime)
        
        // 重置 playhead 到初始位置
        try {
          multitrackRef.current.setTime(initialPlayheadPosition)
          setCurrentTime(initialPlayheadPosition)
          console.log('🔄 Playhead reset to initial position:', {
            currentTime,
            maxDuration,
            initialPlayheadPosition
          })
        } catch (error) {
          console.warn('⚠️ Error resetting playhead position:', error)
        }
        
        return // 停止继续更新
      }
      
      // 性能优化：节流状态更新，降低 React 渲染频率
      const shouldUpdateState = (now - lastStateUpdateTime.current) >= STATE_UPDATE_INTERVAL
      
      // 更新播放时间显示（始终更新）
      if (shouldUpdateState) {
        setCurrentTime(currentTime)
      }
      
      // ========== 轨道1 自动化 ==========
      
      // 【完整自动化已激活：Filter + Volume + EQ】
      // 获取轨道1的 Filter envelope 值（使用 ref 避免依赖数组过大）
      const track1FilterValue = track1FilterEnvelopeRef.current.getValueAtTime(currentTime) * 100  // 0-1 映射到 0-100
      
      // 获取轨道1的 Volume envelope 值（0-1 映射到 -32dB 到 0dB，再减去6dB补偿）
      const track1VolumeValue = track1VolumeEnvelopeRef.current.getValueAtTime(currentTime)
      const track1VolumeDb = (track1VolumeValue * 32) - 32 - 6 // 0->-38dB, 0.5->-22dB, 1->-6dB
      
      // 获取轨道1的 EQ envelope 值（0-1 映射到 -24dB 到 0dB）
      const track1EQLowValue = track1EQLowEnvelopeRef.current.getValueAtTime(currentTime)
      const track1EQMidValue = track1EQMidEnvelopeRef.current.getValueAtTime(currentTime)
      const track1EQHighValue = track1EQHighEnvelopeRef.current.getValueAtTime(currentTime)
      
      const track1EQLowDb = (track1EQLowValue * 24) - 24 // 0->-24dB, 0.5->-12dB, 1->0dB
      const track1EQMidDb = (track1EQMidValue * 24) - 24
      const track1EQHighDb = (track1EQHighValue * 24) - 24
      
      // 更新轨道1 Filter（双滤波器架构）- 音频效果始终更新
      if (track1EffectChainRef.current) {
        const { filterLPF, filterHPF } = track1EffectChainRef.current
        
        if (track1FilterValue === 50) {
          // OFF: 两个滤波器都放开
          filterLPF.frequency.value = 20000
          filterHPF.frequency.value = 20
          if (shouldUpdateState) {
            setRealtimeTrack1FilterFreq(0)
          }
        } else if (track1FilterValue < 50) {
          // LPF 响应
          const normalized = track1FilterValue / 50
          const cutoffHz = calculateCutoffFrequency(normalized)
          filterLPF.frequency.value = Math.max(20, cutoffHz)
          filterHPF.frequency.value = 20  // HPF 保持放开
          if (shouldUpdateState) {
            setRealtimeTrack1FilterFreq(filterLPF.frequency.value)
          }
        } else {
          // HPF 响应
          const normalized = (track1FilterValue - 50) / 50
          const cutoffHz = calculateCutoffFrequency(normalized)
          filterLPF.frequency.value = 20000  // LPF 保持放开
          filterHPF.frequency.value = Math.max(20, cutoffHz)
          if (shouldUpdateState) {
            setRealtimeTrack1FilterFreq(filterHPF.frequency.value)
          }
        }
        
        if (shouldUpdateState) {
          setRealtimeTrack1Filter(track1FilterValue)
        }
        
        const clampedVolumeDb = Math.max(-38, Math.min(-6, track1VolumeDb))
        track1EffectChainRef.current.volume.volume.value = clampedVolumeDb
        if (shouldUpdateState) {
          setRealtimeTrack1Volume(clampedVolumeDb)
        }
        
        track1EffectChainRef.current.eq.low.value = track1EQLowDb
        track1EffectChainRef.current.eq.mid.value = track1EQMidDb
        track1EffectChainRef.current.eq.high.value = track1EQHighDb
        
        if (shouldUpdateState) {
          setRealtimeTrack1EQLow(track1EQLowDb)
          setRealtimeTrack1EQMid(track1EQMidDb)
          setRealtimeTrack1EQHigh(track1EQHighDb)
        }
      }
      
      // ========== 轨道2 自动化（考虑时间偏移）==========
      
      // ⚠️ 重要：Envelope 是基于 timeline 时间定义的（transitionStart, transitionEnd）
      // 而不是基于 track2 的音频内部时间，所以应该使用 currentTime 作为 envelope 时间
      // 如果将来需要 track2 的音频内部时间，可以通过以下公式计算：
      // track2Time = currentTime - nexttrackStartPosition + nexttrackAudioOffset
      const track2EnvelopeTime = currentTime
      
      // 【完整自动化已激活：Filter + Volume + EQ】
      // 获取轨道2的 Filter envelope 值（使用 ref 避免依赖数组过大）
      const track2FilterValue = track2FilterEnvelopeRef.current.getValueAtTime(track2EnvelopeTime) * 100
      
      // 获取轨道2的 Volume envelope 值（0-1 映射到 -32dB 到 0dB，再减去6dB补偿）
      const track2VolumeValue = track2VolumeEnvelopeRef.current.getValueAtTime(track2EnvelopeTime)
      const track2VolumeDb = (track2VolumeValue * 32) - 32 - 6 // 0->-38dB, 0.5->-22dB, 1->-6dB
      
      // 获取轨道2的 EQ envelope 值（0-1 映射到 -24dB 到 0dB）
      const track2EQLowValue = track2EQLowEnvelopeRef.current.getValueAtTime(track2EnvelopeTime)
      const track2EQMidValue = track2EQMidEnvelopeRef.current.getValueAtTime(track2EnvelopeTime)
      const track2EQHighValue = track2EQHighEnvelopeRef.current.getValueAtTime(track2EnvelopeTime)
      
      const track2EQLowDb = (track2EQLowValue * 24) - 24 // 0->-24dB, 0.5->-12dB, 1->0dB
      const track2EQMidDb = (track2EQMidValue * 24) - 24
      const track2EQHighDb = (track2EQHighValue * 24) - 24
      
      // 更新轨道2 Filter（双滤波器架构）- 音频效果始终更新
      if (track2EffectChainRef.current) {
        const { filterLPF, filterHPF } = track2EffectChainRef.current
        
        if (track2FilterValue === 50) {
          // OFF: 两个滤波器都放开
          filterLPF.frequency.value = 20000
          filterHPF.frequency.value = 20
          if (shouldUpdateState) {
            setRealtimeTrack2FilterFreq(0)
          }
        } else if (track2FilterValue < 50) {
          // LPF 响应
          const normalized = track2FilterValue / 50
          const cutoffHz = calculateCutoffFrequency(normalized)
          filterLPF.frequency.value = Math.max(20, cutoffHz)
          filterHPF.frequency.value = 20  // HPF 保持放开
          if (shouldUpdateState) {
            setRealtimeTrack2FilterFreq(filterLPF.frequency.value)
          }
        } else {
          // HPF 响应
          const normalized = (track2FilterValue - 50) / 50
          const cutoffHz = calculateCutoffFrequency(normalized)
          filterLPF.frequency.value = 20000  // LPF 保持放开
          filterHPF.frequency.value = Math.max(20, cutoffHz)
          if (shouldUpdateState) {
            setRealtimeTrack2FilterFreq(filterHPF.frequency.value)
          }
        }
        
        if (shouldUpdateState) {
          setRealtimeTrack2Filter(track2FilterValue)
        }
        
        const clampedVolumeDb = Math.max(-38, Math.min(-6, track2VolumeDb))
        track2EffectChainRef.current.volume.volume.value = clampedVolumeDb
        if (shouldUpdateState) {
          setRealtimeTrack2Volume(clampedVolumeDb)
        }
        
        track2EffectChainRef.current.eq.low.value = track2EQLowDb
        track2EffectChainRef.current.eq.mid.value = track2EQMidDb
        track2EffectChainRef.current.eq.high.value = track2EQHighDb
        
        if (shouldUpdateState) {
          setRealtimeTrack2EQLow(track2EQLowDb)
          setRealtimeTrack2EQMid(track2EQMidDb)
          setRealtimeTrack2EQHigh(track2EQHighDb)
        }
      }
      
      // 更新节流时间戳
      if (shouldUpdateState) {
        lastStateUpdateTime.current = now
        
        // 调用 envelope 更新回调（用于 MIDI 输出）
        if (onEnvelopeUpdate && track1EffectChainRef.current && track2EffectChainRef.current) {
          onEnvelopeUpdate({
            track1: {
              volume: track1VolumeDb,
              filter: track1FilterValue,
              eqLow: track1EQLowDb,
              eqMid: track1EQMidDb,
              eqHigh: track1EQHighDb,
            },
            track2: {
              volume: track2VolumeDb,
              filter: track2FilterValue,
              eqLow: track2EQLowDb,
              eqMid: track2EQMidDb,
              eqHigh: track2EQHighDb,
            }
          })
        }
      }
      
      // 继续下一帧
      animationFrameRef.current = requestAnimationFrame(updateAutomation)
    }
    
    // 开始动画循环
    animationFrameRef.current = requestAnimationFrame(updateAutomation)
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [
    isPlaying,
    maxDuration,
    transitionStart,
    trackPrevBpm,
    // 移除 envelope 依赖，因为已经使用 ref 存储，避免频繁重建动画循环
    nexttrackStartPosition,
    nexttrackAudioOffset,
  ])
  
  // Load multitrack script
  useEffect(() => {
    if (window.Multitrack) {
      setScriptLoaded(true)
      return
    }
    
    const script = document.createElement('script')
    script.src = '/multitrack.min.js'
    script.onload = () => {
      console.log('✅ Multitrack script loaded')
      setScriptLoaded(true)
    }
    script.onerror = () => {
      console.error('❌ Failed to load Multitrack script')
    }
    document.body.appendChild(script)
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [])
  
  // Initialize multitrack
  useEffect(() => {
    if (!containerRef.current || !scriptLoaded || !window.Multitrack) {
      console.log('⏳ Waiting for requirements: container, script, or Multitrack')
      return
    }
    
    setIsLoading(true)
    setCanPlay(false)
    
    // Destroy previous instance
    if (multitrackRef.current) {
      console.log('🔄 Destroying previous multitrack instance')
      multitrackRef.current.destroy()
    }
    
    try {
      // 构建完整的 URL（如果需要的话）
      const apiBaseUrl = getApiBaseUrl()
      const prevtrackFullUrl = prevtrackUrl.startsWith('http') 
        ? prevtrackUrl 
        : `${apiBaseUrl}${prevtrackUrl}`
      const nexttrackFullUrl = nexttrackUrl.startsWith('http')
        ? nexttrackUrl
        : `${apiBaseUrl}${nexttrackUrl}`
      
      // 检测深色模式以设置合适的波形颜色
      const isDarkMode = document.documentElement.classList.contains('dark')
      
      // 将offset从拍数转换为秒
      const beatDuration = 60 / trackPrevBpm
      const track1OffsetSeconds = track1OffsetBeats * beatDuration
      const track2OffsetSeconds = track2OffsetBeats * beatDuration
      
      const tracks = [
        {
          id: 0,
          url: prevtrackFullUrl,
          draggable: true,
          startPosition: 0 + track1OffsetSeconds, // prevtrack 从 0 开始，加上加载的offset
          options: {
            waveColor: isDarkMode ? 'rgba(96, 165, 250, 0.5)' : 'rgba(59, 130, 246, 0.4)', // 蓝色
            progressColor: isDarkMode ? 'rgba(59, 130, 246, 0.9)' : 'rgba(37, 99, 235, 0.85)',
            height: height,
            normalize: true,
            barWidth: 0, // 0 = 正常波形，不是条状
            barGap: 0,
            barRadius: 0,
          }
        },
        {
          id: 1,
          url: nexttrackFullUrl,
          draggable: true,
          startPosition: nexttrackStartPosition + track2OffsetSeconds, // nexttrack 从计算的位置开始，加上加载的offset
          options: {
            waveColor: isDarkMode ? 'rgba(52, 211, 153, 0.5)' : 'rgba(16, 185, 129, 0.4)', // 绿色
            progressColor: isDarkMode ? 'rgba(16, 185, 129, 0.9)' : 'rgba(5, 150, 105, 0.85)',
            height: height,
            normalize: true,
            barWidth: 0, // 0 = 正常波形，不是条状
            barGap: 0,
            barRadius: 0,
          }
        }
      ]
      
      console.log('🎵 Creating playground multitrack with tracks:', tracks)
      console.log('🎨 Dark mode:', isDarkMode)
      
      multitrackRef.current = window.Multitrack.create(tracks, {
        container: containerRef.current,
        minPxPerSec: 10,
        rightButtonDrag: false,
        cursorWidth: 2,
        cursorColor: isDarkMode ? '#60a5fa' : '#3b82f6',
        trackBackground: isDarkMode ? '#111827' : '#ffffff', // 深色模式使用更深的灰色
        trackBorderColor: isDarkMode ? '#1f2937' : '#e5e7eb',
        dragBounds: false, // 禁用拖动边界限制，允许无限拖动
      })
      
      console.log('✅ Playground multitrack instance created')
      
      // Set up event listeners
      multitrackRef.current.on('canplay', () => {
        console.log('✅ Multitrack can play')
        setIsLoading(false)
        setCanPlay(true)
        
        // 获取 duration - 使用 maxDuration 属性
        let duration = 0
        try {
          if (multitrackRef.current.maxDuration) {
            duration = multitrackRef.current.maxDuration
          } else if (multitrackRef.current.durations && multitrackRef.current.durations.length > 0) {
            // 从 durations 数组中获取最大值
            duration = Math.max(...multitrackRef.current.durations)
          } else if (multitrackRef.current.wavesurfers && multitrackRef.current.wavesurfers.length > 0) {
            // 从 wavesurfer 实例获取
            duration = Math.max(...multitrackRef.current.wavesurfers.map((ws: any) => 
              ws?.getDuration?.() || ws?.duration || 0
            ))
          }
        } catch (error) {
          console.warn('⚠️ Error getting duration:', error)
        }
        
        setMaxDuration(duration)
        console.log('⏱️ Duration:', duration)
        
        // 设置初始 playhead 位置为 transitionStart - 8拍
        const eightBeatsTime = 8 * 60 / trackPrevBpm
        const initialPlayheadPosition = Math.max(0, transitionStart - eightBeatsTime)
        
        try {
          multitrackRef.current.setTime(initialPlayheadPosition)
          setCurrentTime(initialPlayheadPosition)
          console.log('🎯 Initial playhead position set to:', {
            transitionStart,
            eightBeatsTime,
            initialPlayheadPosition,
            formula: `${transitionStart.toFixed(2)}s - ${eightBeatsTime.toFixed(2)}s = ${initialPlayheadPosition.toFixed(2)}s`
          })
        } catch (error) {
          console.warn('⚠️ Error setting initial playhead position:', error)
        }
        
        // 尝试连接音频路由到 Tone.js 效果链
        if (!audioRoutingAttempted.current) {
          audioRoutingAttempted.current = true
          setTimeout(() => {
            connectAudioRouting()
          }, 500) // 延迟500ms以确保所有资源已加载
        }
        
        // 备选方案：直接监听 DOM 拖动事件
        setTimeout(() => {
          setupDragListeners()
        }, 1000) // 延迟1秒确保所有元素已渲染
        
        // Draw transition region overlay
        drawTransitionRegion()
      })
      
      multitrackRef.current.on('timeupdate', () => {
        // 当暂停或用户拖动时也更新时间（播放时由 animation frame 更新）
        if (!isPlaying) {
          setCurrentTime(multitrackRef.current.getCurrentTime())
        }
      })
      
      multitrackRef.current.on('play', () => {
        console.log('▶️ Multitrack started playing')
        setIsPlaying(true)
      })
      
      multitrackRef.current.on('pause', () => {
        console.log('⏸️ Multitrack paused')
        setIsPlaying(false)
      })
      
      multitrackRef.current.on('finish', () => {
        console.log('⏹️ Multitrack finished')
        setIsPlaying(false)
        
        // 计算初始 playhead 位置（transitionStart - 8拍）
        const eightBeatsTime = 8 * 60 / trackPrevBpm
        const initialPlayheadPosition = Math.max(0, transitionStart - eightBeatsTime)
        
        // 重置 playhead 到初始位置
        try {
          multitrackRef.current.setTime(initialPlayheadPosition)
          setCurrentTime(initialPlayheadPosition)
          console.log('🔄 Playhead reset to initial position:', initialPlayheadPosition)
        } catch (error) {
          console.warn('⚠️ Error resetting playhead position:', error)
        }
      })
      
      multitrackRef.current.on('error', (error: any) => {
        console.error('❌ Multitrack error:', error)
      })
      
      // 调试：打印所有可用的事件
      console.log('🔍 Multitrack instance:', multitrackRef.current)
      console.log('🔍 Available events:', Object.keys(multitrackRef.current).filter(key => key.includes('on') || key.includes('event')))
      console.log('🔍 Multitrack methods:', Object.keys(multitrackRef.current).filter(key => typeof (multitrackRef.current as any)[key] === 'function'))
      console.log('🔍 Tracks info:', multitrackRef.current.tracks?.map((t: any) => ({
        id: t.id,
        startPosition: t.startPosition,
        draggable: t.draggable
      })))
      
      // 监听轨道拖动事件（实时更新 + 吸附）
      let dragEndTimer: NodeJS.Timeout | null = null
      
      multitrackRef.current.on('start-position-change', (data: any) => {
        console.log('🎯 start-position-change event:', data)
        
        // 注意：data.id 可能是 0，不能用 || 运算符
        const trackId = data.id !== undefined ? data.id : (data.trackId !== undefined ? data.trackId : data.track?.id)
        const startPosition = data.startPosition !== undefined ? data.startPosition : data.position
        
        console.log('🔍 Parsed values:', { trackId, startPosition, trackIdType: typeof trackId, startPositionType: typeof startPosition })
        
        if (trackId !== undefined && startPosition !== undefined) {
          // 实时更新：计算当前拖动的偏移量（秒）
          const offsetSeconds = trackId === 0 ? startPosition : startPosition - nexttrackStartPosition
          
          // 转换为拍数
          const beatDuration = 60 / trackPrevBpm
          const offsetBeats = offsetSeconds / beatDuration
          
          // 更新对应轨道的offset（实时更新，不吸附）
          if (trackId === 0) {
            setTrack1OffsetBeats(offsetBeats)
          } else if (trackId === 1) {
            setTrack2OffsetBeats(offsetBeats)
          }
          
          console.log(`🎯 Track ${trackId} position changing:`, {
            startPosition,
            offsetSeconds,
            offsetBeats: offsetBeats.toFixed(2),
            beatDuration
          })
          
          // 吸附逻辑：清除之前的定时器
          if (dragEndTimer) {
            clearTimeout(dragEndTimer)
          }
          
          // 设置新的定时器：300ms后如果没有新的拖动事件，认为拖动结束，执行吸附
          dragEndTimer = setTimeout(() => {
            // 吸附到最近的整数拍
            const snappedBeats = Math.round(offsetBeats)
            const snappedSeconds = snappedBeats * beatDuration
            
            // 计算吸附后的 startPosition
            const snappedStartPosition = trackId === 0 
              ? snappedSeconds 
              : nexttrackStartPosition + snappedSeconds
            
            // 检查是否需要吸附（如果偏差超过0.01秒才吸附）
            const needsSnap = Math.abs(startPosition - snappedStartPosition) > 0.01
            
            if (needsSnap && multitrackRef.current) {
              try {
                // 使用 setTrackStartPosition 方法（如果存在）
                if (typeof multitrackRef.current.setTrackStartPosition === 'function') {
                  multitrackRef.current.setTrackStartPosition(trackId, snappedStartPosition)
                  console.log(`✨ Track ${trackId} position snapped using setTrackStartPosition to ${snappedBeats} beats`)
                } else if (multitrackRef.current.tracks && multitrackRef.current.tracks[trackId]) {
                  // 手动更新
                  const track = multitrackRef.current.tracks[trackId]
                  track.startPosition = snappedStartPosition
                  
                  // 强制重新渲染
                  const container = containerRef.current
                  if (container && multitrackRef.current.wavesurfers && multitrackRef.current.wavesurfers[trackId]) {
                    const ws = multitrackRef.current.wavesurfers[trackId]
                    const duration = maxDuration || ws.getDuration?.() || 0
                    
                    if (duration > 0 && container.clientWidth > 0) {
                      const pixelsPerSecond = container.clientWidth / duration
                      const leftPx = snappedStartPosition * pixelsPerSecond
                      
                      const wrapper = ws.drawer?.wrapper || ws.container?.parentElement
                      if (wrapper && wrapper.style) {
                        wrapper.style.left = `${leftPx}px`
                      }
                    }
                  }
                  console.log(`✨ Track ${trackId} position snapped manually to ${snappedBeats} beats`)
                }
              } catch (error) {
                console.warn('⚠️ Error snapping track position:', error)
              }
            }
            
            // 更新状态到吸附值
            if (trackId === 0) {
              setTrack1OffsetBeats(snappedBeats)
            } else if (trackId === 1) {
              setTrack2OffsetBeats(snappedBeats)
            }
            
            console.log(`🎯 Track ${trackId} drag ended (snapped):`, {
              original: { offsetBeats: offsetBeats.toFixed(2), offsetSeconds: offsetSeconds.toFixed(3) },
              snapped: { beats: snappedBeats, seconds: snappedSeconds.toFixed(3), startPosition: snappedStartPosition.toFixed(3) },
              needsSnap
            })
            
            dragEndTimer = null
          }, 300)
        } else {
          console.warn('⚠️ Could not parse trackId or startPosition from event data:', data)
        }
      })
      
    } catch (error) {
      console.error('❌ Error creating multitrack:', error)
      setIsLoading(false)
      alert(`播放器初始化失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
    
    return () => {
      if (multitrackRef.current) {
        console.log('🧹 Cleaning up multitrack instance')
        multitrackRef.current.destroy()
      }
    }
  }, [prevtrackUrl, nexttrackUrl, scriptLoaded, height, nexttrackStartPosition])
  
  // Draw transition region overlay
  const drawTransitionRegion = useCallback((customStart?: number, customEnd?: number) => {
    if (!canvasOverlayRef.current || !containerRef.current) return
    
    const canvas = canvasOverlayRef.current
    const container = containerRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    
    // Set canvas size to match container
    canvas.width = container.clientWidth
    canvas.height = container.clientHeight
    
    const duration = maxDuration
    if (!duration) {
      console.log('⏳ No duration yet, skipping overlay draw')
      return
    }
    
    const pixelsPerSecond = canvas.width / duration
    
    // Calculate pixel positions - 使用自定义值或默认值
    const startTime = customStart !== undefined ? customStart : transitionStart
    const endTime = customEnd !== undefined ? customEnd : transitionEnd
    const transitionStartPx = startTime * pixelsPerSecond
    const transitionEndPx = endTime * pixelsPerSecond
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // 检测深色模式
    const isDarkMode = document.documentElement.classList.contains('dark')
    
    // Draw shadow overlay on regions outside transition
    // 深色模式：更深的遮罩；浅色模式：更浅的遮罩
    ctx.fillStyle = isDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.15)'
    
    // Left shadow (before transition)
    ctx.fillRect(0, 0, transitionStartPx, canvas.height)
    
    // Right shadow (after transition)
    ctx.fillRect(transitionEndPx, 0, canvas.width - transitionEndPx, canvas.height)
    
    // Draw transition region borders
    // 深色模式：更亮的绿色；浅色模式：更深的绿色以确保对比度
    ctx.strokeStyle = isDarkMode ? '#34d399' : '#059669'
    ctx.lineWidth = 2
    ctx.setLineDash([5, 5])
    
    // Left border
    ctx.beginPath()
    ctx.moveTo(transitionStartPx, 0)
    ctx.lineTo(transitionStartPx, canvas.height)
    ctx.stroke()
    
    // Right border
    ctx.beginPath()
    ctx.moveTo(transitionEndPx, 0)
    ctx.lineTo(transitionEndPx, canvas.height)
    ctx.stroke()
    
    console.log('🎨 Transition region overlay drawn:', {
      transitionStartPx,
      transitionEndPx,
      duration,
      pixelsPerSecond
    })
  }, [transitionStart, transitionEnd, maxDuration])
  
  // 移动转场区域的函数（以4拍为单位）
  const moveTransitionRegion = useCallback((direction: 'left' | 'right') => {
    const step = 4 // 移动单位为4拍
    const newOffset = direction === 'left' 
      ? transitionOffsetBeats - step 
      : transitionOffsetBeats + step
    
    // 确保转场区域不会移动到负数位置
    const minOffset = -32 // 转场区域最早从第0拍开始（32 + minOffset = 0）
    if (newOffset < minOffset) {
      console.warn(`⚠️ 转场区域不能移动到第 ${32 + newOffset} 拍之前`)
      return
    }
    
    console.log(`🔀 移动转场区域: ${direction === 'left' ? '←' : '→'} ${step} beats`)
    console.log(`   偏移量: ${transitionOffsetBeats} → ${newOffset} beats`)
    
    setTransitionOffsetBeats(newOffset)
    
    // 重新计算转场区域
    const newTransitionStart = (32 + newOffset) * 60 / trackPrevBpm
    const newTransitionEnd = (32 + nbeats + newOffset) * 60 / trackPrevBpm
    
    console.log('📐 新的转场区域:', {
      transitionStart: newTransitionStart,
      transitionEnd: newTransitionEnd,
      offsetBeats: newOffset
    })
    
    // 重新加载 Volume envelope
    if (volumeCurvePreset) {
      const volumeEnvelopes = AutomationPresetLoader.loadVolumeCurve(
        volumeCurvePreset,
        newTransitionStart,
        newTransitionEnd
      )
      if (volumeEnvelopes) {
        setTrack1VolumeEnvelope(volumeEnvelopes.track1Volume)
        setTrack2VolumeEnvelope(volumeEnvelopes.track2Volume)
        console.log('✅ Volume envelope 已更新')
      }
    }
    
    // 重新加载 EQ envelope
    if (eqCurvePreset) {
      const eqEnvelopes = AutomationPresetLoader.loadEQCurve(
        eqCurvePreset,
        newTransitionStart,
        newTransitionEnd
      )
      if (eqEnvelopes) {
        setTrack1EQLowEnvelope(eqEnvelopes.track1EQLow)
        setTrack1EQMidEnvelope(eqEnvelopes.track1EQMid)
        setTrack1EQHighEnvelope(eqEnvelopes.track1EQHigh)
        setTrack2EQLowEnvelope(eqEnvelopes.track2EQLow)
        setTrack2EQMidEnvelope(eqEnvelopes.track2EQMid)
        setTrack2EQHighEnvelope(eqEnvelopes.track2EQHigh)
        console.log('✅ EQ envelope 已更新')
      }
    }
    
    // 重新加载 Filter envelope
    if (filterTypePreset) {
      const filterEnvelopes = AutomationPresetLoader.loadFilterType(
        filterTypePreset,
        newTransitionStart,
        newTransitionEnd
      )
      if (filterEnvelopes) {
        setTrack1FilterEnvelope(filterEnvelopes.track1Filter)
        setTrack2FilterEnvelope(filterEnvelopes.track2Filter)
        console.log('✅ Filter envelope 已更新')
      }
    }
    
    // 立即使用新的转场区域时间重新绘制（不等待状态更新）
    requestAnimationFrame(() => {
      drawTransitionRegion(newTransitionStart, newTransitionEnd)
    })
  }, [transitionOffsetBeats, trackPrevBpm, nbeats, volumeCurvePreset, eqCurvePreset, filterTypePreset, drawTransitionRegion])
  
  // 当 nbeats 改变时，重新计算转场区域和 envelope（不重新加载播放器）
  useEffect(() => {
    // 如果 nbeats 没有变化，不执行更新
    if (prevNbeatsRef.current === nbeats) {
      return
    }
    
    console.log(`🔄 nbeats 变化检测: ${prevNbeatsRef.current} → ${nbeats}`)
    prevNbeatsRef.current = nbeats
    
    // 重新计算转场区域（应用偏移量）
    const newTransitionStart = (32 + transitionOffsetBeats) * 60 / trackPrevBpm
    const newTransitionEnd = (32 + nbeats + transitionOffsetBeats) * 60 / trackPrevBpm
    
    console.log('📐 重新计算转场区域:', {
      transitionStart: newTransitionStart,
      transitionEnd: newTransitionEnd,
      nbeats,
      offsetBeats: transitionOffsetBeats
    })
    
    // 重新加载 Volume envelope
    if (volumeCurvePreset) {
      const volumeEnvelopes = AutomationPresetLoader.loadVolumeCurve(
        volumeCurvePreset,
        newTransitionStart,
        newTransitionEnd
      )
      if (volumeEnvelopes) {
        setTrack1VolumeEnvelope(volumeEnvelopes.track1Volume)
        setTrack2VolumeEnvelope(volumeEnvelopes.track2Volume)
        console.log('✅ Volume envelope 已更新')
      }
    }
    
    // 重新加载 EQ envelope
    if (eqCurvePreset) {
      const eqEnvelopes = AutomationPresetLoader.loadEQCurve(
        eqCurvePreset,
        newTransitionStart,
        newTransitionEnd
      )
      if (eqEnvelopes) {
        setTrack1EQLowEnvelope(eqEnvelopes.track1EQLow)
        setTrack1EQMidEnvelope(eqEnvelopes.track1EQMid)
        setTrack1EQHighEnvelope(eqEnvelopes.track1EQHigh)
        setTrack2EQLowEnvelope(eqEnvelopes.track2EQLow)
        setTrack2EQMidEnvelope(eqEnvelopes.track2EQMid)
        setTrack2EQHighEnvelope(eqEnvelopes.track2EQHigh)
        console.log('✅ EQ envelope 已更新')
      }
    }
    
    // 重新加载 Filter envelope
    if (filterTypePreset) {
      const filterEnvelopes = AutomationPresetLoader.loadFilterType(
        filterTypePreset,
        newTransitionStart,
        newTransitionEnd
      )
      if (filterEnvelopes) {
        setTrack1FilterEnvelope(filterEnvelopes.track1Filter)
        setTrack2FilterEnvelope(filterEnvelopes.track2Filter)
        console.log('✅ Filter envelope 已更新')
      }
    }
    
    // 重新绘制转场区域覆盖层
    if (canPlay) {
      setTimeout(() => {
        drawTransitionRegion()
      }, 100)
    }
    
  }, [nbeats, trackPrevBpm, transitionOffsetBeats, volumeCurvePreset, eqCurvePreset, filterTypePreset, canPlay, drawTransitionRegion])
  
  // Redraw overlay when player resizes or duration changes
  useEffect(() => {
    if (canPlay) {
      drawTransitionRegion()
      
      // Redraw on window resize
      const handleResize = () => drawTransitionRegion()
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [canPlay, drawTransitionRegion])
  
  const handlePlayPause = useCallback(async () => {
    if (!multitrackRef.current || !canPlay) return
    
    // 确保 Tone.js 音频上下文已启动（需要用户交互）
    if (Tone.context.state !== 'running') {
      await Tone.start()
      console.log('🎵 Tone.js Audio Context resumed')
    }
    
    if (isPlaying) {
      multitrackRef.current.pause()
      setIsPlaying(false) // 立即更新状态
    } else {
      multitrackRef.current.play()
      setIsPlaying(true) // 立即更新状态
    }
  }, [isPlaying, canPlay])
  
  // Seek 前进/后退（以秒为单位）
  const handleSeek = useCallback((direction: 'forward' | 'backward') => {
    if (!multitrackRef.current || !canPlay) return
    
    const currentTime = multitrackRef.current.getCurrentTime()
    const seekAmount = 5 // 5秒
    const newTime = direction === 'forward' 
      ? Math.min(currentTime + seekAmount, maxDuration)
      : Math.max(currentTime - seekAmount, 0)
    
    multitrackRef.current.setTime(newTime)
    setCurrentTime(newTime)
    console.log(`⏩ Seeked ${direction}: ${currentTime.toFixed(2)}s -> ${newTime.toFixed(2)}s`)
  }, [canPlay, maxDuration])
  
  // 静音控制
  const handleMute = useCallback((state: 'both' | 'track0' | 'track1') => {
    if (!multitrackRef.current || !canPlay) return
    
    const wavesurfers = multitrackRef.current.wavesurfers
    if (!wavesurfers || wavesurfers.length < 2) return
    
    setMuteState(state)
    
    switch (state) {
      case 'both':
        wavesurfers[0]?.setVolume?.(1)
        wavesurfers[1]?.setVolume?.(1)
        console.log('🔊 Both tracks unmuted')
        break
      case 'track0':
        wavesurfers[0]?.setVolume?.(0)
        wavesurfers[1]?.setVolume?.(1)
        console.log('🔇 Track 0 muted, Track 1 playing')
        break
      case 'track1':
        wavesurfers[0]?.setVolume?.(1)
        wavesurfers[1]?.setVolume?.(0)
        console.log('🔇 Track 1 muted, Track 0 playing')
        break
    }
  }, [canPlay])
  
  // 键盘快捷键监听器
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // 忽略在输入框中的按键
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return
      }
      
      switch (event.code) {
        case 'Space':
          event.preventDefault()
          console.log('⌨️ Space key pressed - toggling play/pause')
          handlePlayPause()
          break
        case 'KeyA':
          event.preventDefault()
          console.log('⌨️ A key pressed - seeking backward')
          handleSeek('backward')
          break
        case 'KeyD':
          event.preventDefault()
          console.log('⌨️ D key pressed - seeking forward')
          handleSeek('forward')
          break
        case 'Digit1':
          event.preventDefault()
          console.log('⌨️ 1 key pressed - muting track 0 (prevtrack)')
          handleMute('track0')
          break
        case 'Digit2':
          event.preventDefault()
          console.log('⌨️ 2 key pressed - muting track 1 (nexttrack)')
          handleMute('track1')
          break
        case 'Digit3':
          event.preventDefault()
          console.log('⌨️ 3 key pressed - unmuting both tracks')
          handleMute('both')
          break
        case 'ArrowLeft':
          event.preventDefault()
          console.log('⌨️ Left arrow pressed - moving transition region left')
          moveTransitionRegion('left')
          break
        case 'ArrowRight':
          event.preventDefault()
          console.log('⌨️ Right arrow pressed - moving transition region right')
          moveTransitionRegion('right')
          break
        default:
          break
      }
    }
    
    // 添加全局键盘事件监听器
    document.addEventListener('keydown', handleKeyDown)
    console.log('⌨️ Keyboard shortcuts registered')
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      console.log('⌨️ Keyboard shortcuts unregistered')
    }
  }, [canPlay, muteState, handlePlayPause, handleSeek, handleMute, moveTransitionRegion])
  
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }
  
  // 计算当前播放位置相对于转场边界的位置（毫秒）
  const getTransitionPosition = (): { 
    fromStart: number
    fromEnd: number
    status: 'before' | 'in' | 'after'
  } => {
    const currentMs = currentTime * 1000
    const transitionStartMs = transitionStart * 1000
    const transitionEndMs = transitionEnd * 1000
    
    if (currentMs < transitionStartMs) {
      return {
        fromStart: currentMs - transitionStartMs, // 负数表示在转场开始之前
        fromEnd: currentMs - transitionEndMs,
        status: 'before'
      }
    } else if (currentMs >= transitionStartMs && currentMs <= transitionEndMs) {
      return {
        fromStart: currentMs - transitionStartMs, // 正数表示进入转场区域多久
        fromEnd: currentMs - transitionEndMs, // 负数表示距离转场结束还有多久
        status: 'in'
      }
    } else {
      return {
        fromStart: currentMs - transitionStartMs,
        fromEnd: currentMs - transitionEndMs, // 正数表示已离开转场区域
        status: 'after'
      }
    }
  }
  
  const transitionPos = getTransitionPosition()
  
  return (
    <div className="w-full">
      {/* 控制条 */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <button
          onClick={handlePlayPause}
          disabled={!canPlay || isLoading}
          className="p-3 rounded-full bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md active:scale-95"
        >
          {isPlaying ? <Pause size={20} /> : <Play size={20} />}
        </button>
        
        {/* 播放进度数字显示 */}
        <div className="flex items-center gap-2 text-sm font-mono text-gray-700 dark:text-gray-300">
          <span className="font-semibold">{formatTime(currentTime)}</span>
          <span className="text-gray-400 dark:text-gray-500">/</span>
          <span className="text-gray-500 dark:text-gray-400">{formatTime(maxDuration)}</span>
        </div>
        
        {/* 静音状态指示 */}
        <div className="flex items-center gap-2 border-l border-gray-300 dark:border-gray-600 pl-4">
          <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">轨道:</span>
          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${
            muteState === 'track1' || muteState === 'both' 
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' 
              : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
          }`}>
            {muteState === 'track1' || muteState === 'both' ? '🔊' : '🔇'} T1
          </div>
          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${
            muteState === 'track0' || muteState === 'both' 
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
              : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
          }`}>
            {muteState === 'track0' || muteState === 'both' ? '🔊' : '🔇'} T2
          </div>
        </div>
        
        {/* 转场边界相对位置显示 */}
        <div className="flex items-center gap-3 text-xs border-l border-gray-300 dark:border-gray-600 pl-4">
          {/* <div className={`px-2 py-1 rounded font-medium ${
            transitionPos.status === 'before' 
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
              : transitionPos.status === 'in'
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}>
            {transitionPos.status === 'before' && 'Before Transition'}
            {transitionPos.status === 'in' && 'During Transition'}
            {transitionPos.status === 'after' && 'After Transition'}
          </div> */}
          
          <div className="flex flex-col gap-0.5 font-mono">
            <div className="flex items-center gap-1">
              <span className="text-gray-500 dark:text-gray-400">E.T.T.S.:</span>
              <span className={`font-semibold ${
                transitionPos.fromStart < 0 
                  ? 'text-blue-600 dark:text-blue-400' 
                  : 'text-green-600 dark:text-green-400'
              }`}>
                {transitionPos.fromStart >= 0 ? '+' : ''}{Math.round(transitionPos.fromStart)}ms
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500 dark:text-gray-400">E.T.T.E.:</span>
              <span className={`font-semibold ${
                transitionPos.fromEnd < 0 
                  ? 'text-orange-600 dark:text-orange-400' 
                  : 'text-gray-600 dark:text-gray-400'
              }`}>
                {transitionPos.fromEnd >= 0 ? '+' : ''}{Math.round(transitionPos.fromEnd)}ms
              </span>
            </div>
          </div>
        </div>
        
        {/* 转场区域移动控制 */}
        <div className="flex items-center gap-2 border-l border-gray-300 dark:border-gray-600 pl-4">
          <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">转场区域:</span>
          <button
            onClick={() => moveTransitionRegion('left')}
            disabled={!canPlay || isLoading}
            className="flex items-center gap-1 px-2 py-1 rounded bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md active:scale-95"
            title="向左移动4拍"
          >
            <ChevronLeft size={14} />
            <span>4拍</span>
          </button>
          <button
            onClick={() => moveTransitionRegion('right')}
            disabled={!canPlay || isLoading}
            className="flex items-center gap-1 px-2 py-1 rounded bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md active:scale-95"
            title="向右移动4拍"
          >
            <span>4拍</span>
            <ChevronRight size={14} />
          </button>
          <div className="flex items-center gap-1 px-2 py-1 rounded bg-orange-50 dark:bg-orange-900/30">
            <span className="text-orange-700 dark:text-orange-300 text-xs font-semibold">偏移:</span>
            <span className="font-mono text-orange-900 dark:text-orange-100 text-xs font-bold">
              {transitionOffsetBeats > 0 ? '+' : ''}{transitionOffsetBeats} 拍
            </span>
          </div>
        </div>
        
        {/* 轨道偏移量显示 */}
        <div className="flex items-center gap-3 text-xs border-l border-gray-300 dark:border-gray-600 pl-4">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400 font-medium">Track Offsets:</span>
            <div className="flex items-center gap-1 px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/30">
              <span className="text-blue-700 dark:text-blue-300 font-semibold">T1:</span>
              <span className="font-mono text-blue-900 dark:text-blue-100 font-bold">
                {track1OffsetBeats.toFixed(1)} beats
              </span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 rounded bg-green-50 dark:bg-green-900/30">
              <span className="text-green-700 dark:text-green-300 font-semibold">T2:</span>
              <span className="font-mono text-green-900 dark:text-green-100 font-bold">
                {track2OffsetBeats.toFixed(1)} beats
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* 实时 Envelope 值监测 - 超紧凑单行显示 */}
      <div className="mb-4 p-2 bg-gradient-to-br from-purple-50 via-blue-50 to-yellow-50 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-yellow-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          {/* 轨道1 - 单行显示 */}
          <div className="flex items-center gap-2 bg-white/60 dark:bg-gray-800/40 rounded px-2 py-1">
            <span className="text-blue-700 dark:text-blue-400 font-semibold">Track1:</span>
            <span className="text-purple-600 dark:text-purple-400 font-medium">Filter:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {realtimeTrack1Filter === 50 ? 'OFF' : realtimeTrack1Filter < 50 ? `LP${(50 - realtimeTrack1Filter).toFixed(0)}%` : `HP${(realtimeTrack1Filter - 50).toFixed(0)}%`}
              {realtimeTrack1FilterFreq > 0 && <span className="text-gray-500 dark:text-gray-400">({realtimeTrack1FilterFreq.toFixed(0)}Hz)</span>}
            </span>
            <span className="text-gray-400">|</span>
            <span className="text-blue-600 dark:text-blue-400 font-medium">Volume:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {realtimeTrack1Volume > 0 ? '+' : ''}{realtimeTrack1Volume.toFixed(1)}dB
            </span>
            <span className="text-gray-400">|</span>
            <span className="text-yellow-600 dark:text-yellow-400 font-medium">EQ:</span>
            <span className="text-gray-100 dark:text-gray-300 font-medium">Low:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {realtimeTrack1EQLow > 0 ? '+' : ''}{realtimeTrack1EQLow.toFixed(1)}
            </span>
            <span className="text-gray-100 dark:text-gray-300 font-medium">Mid:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {realtimeTrack1EQMid > 0 ? '+' : ''}{realtimeTrack1EQMid.toFixed(1)}
            </span>
            <span className="text-gray-100 dark:text-gray-300 font-medium">High:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {realtimeTrack1EQHigh > 0 ? '+' : ''}{realtimeTrack1EQHigh.toFixed(1)}
            </span>
          </div>
          
          {/* 轨道2 - 单行显示 */}
          <div className="flex items-center gap-2 bg-white/60 dark:bg-gray-800/40 rounded px-2 py-1">
            <span className="text-green-700 dark:text-green-400 font-semibold">Track2:</span>
            <span className="text-purple-600 dark:text-purple-400 font-medium">Filter:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {realtimeTrack2Filter === 50 ? 'OFF' : realtimeTrack2Filter < 50 ? `LP${(50 - realtimeTrack2Filter).toFixed(0)}%` : `HP${(realtimeTrack2Filter - 50).toFixed(0)}%`}
              {realtimeTrack2FilterFreq > 0 && <span className="text-gray-500 dark:text-gray-400">({realtimeTrack2FilterFreq.toFixed(0)}Hz)</span>}
            </span>
            <span className="text-gray-400">|</span>
            <span className="text-blue-600 dark:text-blue-400 font-medium">Volume:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {realtimeTrack2Volume > 0 ? '+' : ''}{realtimeTrack2Volume.toFixed(1)}dB
            </span>
            <span className="text-gray-400">|</span>
            <span className="text-yellow-600 dark:text-yellow-400 font-medium">EQ:</span>
            <span className="text-gray-100 dark:text-gray-300 font-medium">Low:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {realtimeTrack2EQLow > 0 ? '+' : ''}{realtimeTrack2EQLow.toFixed(1)}
            </span>
            <span className="text-gray-100 dark:text-gray-300 font-medium">Mid:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {realtimeTrack2EQMid > 0 ? '+' : ''}{realtimeTrack2EQMid.toFixed(1)}
            </span>
            <span className="text-gray-100 dark:text-gray-300 font-medium">High:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {realtimeTrack2EQHigh > 0 ? '+' : ''}{realtimeTrack2EQHigh.toFixed(1)}
            </span>
          </div>
        </div>
        
        {/* 效果链状态显示 */}
        {(track1EQ || track2EQ) && (
          <div className="mt-4 pt-3 border-t border-purple-200 dark:border-purple-700 space-y-2">
            {/* 效果链状态 */}
            <div className="flex items-center gap-3 text-xs">
              <span className="font-semibold text-gray-900 dark:text-gray-200">🎛️ Effect Chain:</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                eqActive 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
              }`}>
                {eqActive ? '✅ Connected' : '⏳ Connecting'}
              </span>
              {eqActive && (
                <span className="text-xs text-gray-600 dark:text-gray-400 font-mono">
                  Audio → EQ → Filter → Volume → Output
                </span>
              )}
            </div>
            
            {/* 提示信息或错误 */}
            {!eqActive && (
              <div className="flex items-center justify-between gap-2">
                <div className={`text-xs px-2 py-1 rounded flex-1 ${
                  routingError 
                    ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
                    : 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
                }`}>
                  {routingError ? (
                    <>❌ 路由失败: {routingError}</>
                  ) : (
                    <>💡 音频路由将在播放器加载完成后自动连接</>
                  )}
                </div>
                {canPlay && (
                  <button
                    onClick={() => {
                      audioRoutingAttempted.current = false
                      connectAudioRouting()
                    }}
                    className="text-xs px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                  >
                    🔄 重试连接
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* 波形容器 */}
      <div className="relative bg-white dark:bg-[#111827] rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700 shadow-sm">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 backdrop-blur-sm z-20">
            <div className="text-center space-y-6 p-8">
              {/* 主加载动画 */}
              <div className="relative w-20 h-20 mx-auto">
                {/* 外圈旋转 */}
                <div className="absolute inset-0 rounded-full border-4 border-blue-200 dark:border-blue-900"></div>
                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 dark:border-t-blue-400 animate-spin"></div>
                
                {/* 内圈反向旋转 */}
                <div className="absolute inset-2 rounded-full border-4 border-purple-200 dark:border-purple-900"></div>
                <div className="absolute inset-2 rounded-full border-4 border-transparent border-b-purple-600 dark:border-b-purple-400 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1s' }}></div>
                
                {/* 中心音符图标 */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                  </svg>
                </div>
              </div>
              
              {/* 加载文本 */}
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  🎵 正在加载转场播放器
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  正在准备音频资源和效果链...
                </p>
              </div>
              
              {/* 加载状态指示器 */}
              <div className="flex flex-col items-center gap-3 pt-4">
                <div className="flex items-center gap-2 text-xs">
                  <div className={`w-2 h-2 rounded-full ${scriptLoaded ? 'bg-green-500 animate-pulse' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                  <span className={scriptLoaded ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-500 dark:text-gray-400'}>
                    {scriptLoaded ? '✓ 脚本已加载' : '⏳ 加载脚本中...'}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-xs">
                  <div className={`w-2 h-2 rounded-full ${isToneInitialized.current ? 'bg-green-500 animate-pulse' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                  <span className={isToneInitialized.current ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-500 dark:text-gray-400'}>
                    {isToneInitialized.current ? '✓ 效果链已初始化' : '⏳ 初始化效果链...'}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-xs">
                  <div className={`w-2 h-2 rounded-full ${canPlay ? 'bg-green-500 animate-pulse' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                  <span className={canPlay ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-500 dark:text-gray-400'}>
                    {canPlay ? '✓ 音频已准备' : '⏳ 加载音频文件...'}
                  </span>
                </div>
              </div>
              
              {/* 进度条 */}
              <div className="w-64 mx-auto">
                <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-300 ease-out"
                    style={{ 
                      width: `${(
                        (scriptLoaded ? 33 : 0) + 
                        (isToneInitialized.current ? 33 : 0) + 
                        (canPlay ? 34 : 0)
                      )}%`
                    }}
                  ></div>
                </div>
              </div>
              
              {/* 提示信息 */}
              <p className="text-xs text-gray-500 dark:text-gray-500 max-w-md">
                💡 首次加载可能需要几秒钟，请耐心等待
              </p>
            </div>
          </div>
        )}
        
        <div ref={containerRef} className="relative" />
        
        {/* Canvas overlay for transition region */}
        <canvas
          ref={canvasOverlayRef}
          className="absolute top-0 left-0 pointer-events-none"
          style={{ zIndex: 10 }}
        />
        
        {/* Automation Envelope Overlays - 只显示，不可交互，点击穿透到波形 */}
        {canPlay && containerRef.current && (
          <>
            {/* ========== 轨道1 (prevtrack) Automation Envelopes ========== */}
            
            {/* Filter Envelope - 紫色，向上偏移 */}
            <AutomationEnvelopeOverlay
              trackIndex={0}
              trackColor="rgba(168, 85, 247, 0.8)"
              envelope={track1FilterEnvelope}
              onEnvelopeChange={(newEnvelope) => setTrack1FilterEnvelope(newEnvelope)}
              containerWidth={containerRef.current.clientWidth}
              containerHeight={height * 2}
              duration={maxDuration}
              trackOffset={0}
              transitionStart={transitionStart}
              transitionEnd={transitionEnd}
              enabled={false}
              showPoints={false}
              showCurve={true}
              verticalShift={0.04}
            />
            
            {/* Volume Envelope - 蓝色，无偏移（居中） */}
            <AutomationEnvelopeOverlay
              trackIndex={0}
              trackColor="rgba(59, 130, 246, 0.8)"
              envelope={track1VolumeEnvelope}
              onEnvelopeChange={(newEnvelope) => setTrack1VolumeEnvelope(newEnvelope)}
              containerWidth={containerRef.current.clientWidth}
              containerHeight={height * 2}
              duration={maxDuration}
              trackOffset={0}
              transitionStart={transitionStart}
              transitionEnd={transitionEnd}
              enabled={false}
              showPoints={false}
              showCurve={true}
              verticalShift={0}
            />
            
            {/* EQ Low Envelope - 深黄色，向下偏移 */}
            <AutomationEnvelopeOverlay
              trackIndex={0}
              trackColor="rgba(234, 179, 8, 0.8)"
              envelope={track1EQLowEnvelope}
              onEnvelopeChange={(newEnvelope) => setTrack1EQLowEnvelope(newEnvelope)}
              containerWidth={containerRef.current.clientWidth}
              containerHeight={height * 2}
              duration={maxDuration}
              trackOffset={0}
              transitionStart={transitionStart}
              transitionEnd={transitionEnd}
              enabled={false}
              showPoints={false}
              showCurve={true}
              verticalShift={-0.04}
            />
            
            {/* EQ Mid Envelope - 中黄色，向下偏移 */}
            <AutomationEnvelopeOverlay
              trackIndex={0}
              trackColor="rgba(250, 204, 21, 0.8)"
              envelope={track1EQMidEnvelope}
              onEnvelopeChange={(newEnvelope) => setTrack1EQMidEnvelope(newEnvelope)}
              containerWidth={containerRef.current.clientWidth}
              containerHeight={height * 2}
              duration={maxDuration}
              trackOffset={0}
              transitionStart={transitionStart}
              transitionEnd={transitionEnd}
              enabled={false}
              showPoints={false}
              showCurve={true}
              verticalShift={-0.04}
            />
            
            {/* EQ High Envelope - 浅黄色，向下偏移 */}
            <AutomationEnvelopeOverlay
              trackIndex={0}
              trackColor="rgba(253, 224, 71, 0.8)"
              envelope={track1EQHighEnvelope}
              onEnvelopeChange={(newEnvelope) => setTrack1EQHighEnvelope(newEnvelope)}
              containerWidth={containerRef.current.clientWidth}
              containerHeight={height * 2}
              duration={maxDuration}
              trackOffset={0}
              transitionStart={transitionStart}
              transitionEnd={transitionEnd}
              enabled={false}
              showPoints={false}
              showCurve={true}
              verticalShift={-0.04}
            />
            
            {/* ========== 轨道2 (nexttrack) Automation Envelopes ========== */}
            
            {/* Filter Envelope - 紫色，向上偏移 */}
            <AutomationEnvelopeOverlay
              trackIndex={1}
              trackColor="rgba(168, 85, 247, 0.8)"
              envelope={track2FilterEnvelope}
              onEnvelopeChange={(newEnvelope) => setTrack2FilterEnvelope(newEnvelope)}
              containerWidth={containerRef.current.clientWidth}
              containerHeight={height * 2}
              duration={maxDuration}
              trackOffset={height}
              transitionStart={transitionStart}
              transitionEnd={transitionEnd}
              enabled={false}
              showPoints={false}
              showCurve={true}
              verticalShift={0.04}
            />
            
            {/* Volume Envelope - 蓝色，无偏移（居中） */}
            <AutomationEnvelopeOverlay
              trackIndex={1}
              trackColor="rgba(59, 130, 246, 0.8)"
              envelope={track2VolumeEnvelope}
              onEnvelopeChange={(newEnvelope) => setTrack2VolumeEnvelope(newEnvelope)}
              containerWidth={containerRef.current.clientWidth}
              containerHeight={height * 2}
              duration={maxDuration}
              trackOffset={height}
              transitionStart={transitionStart}
              transitionEnd={transitionEnd}
              enabled={false}
              showPoints={false}
              showCurve={true}
              verticalShift={0}
            />
            
            {/* EQ Low Envelope - 深黄色，向下偏移 */}
            <AutomationEnvelopeOverlay
              trackIndex={1}
              trackColor="rgba(234, 179, 8, 0.8)"
              envelope={track2EQLowEnvelope}
              onEnvelopeChange={(newEnvelope) => setTrack2EQLowEnvelope(newEnvelope)}
              containerWidth={containerRef.current.clientWidth}
              containerHeight={height * 2}
              duration={maxDuration}
              trackOffset={height}
              transitionStart={transitionStart}
              transitionEnd={transitionEnd}
              enabled={false}
              showPoints={false}
              showCurve={true}
              verticalShift={-0.04}
            />
            
            {/* EQ Mid Envelope - 中黄色，向下偏移 */}
            <AutomationEnvelopeOverlay
              trackIndex={1}
              trackColor="rgba(250, 204, 21, 0.8)"
              envelope={track2EQMidEnvelope}
              onEnvelopeChange={(newEnvelope) => setTrack2EQMidEnvelope(newEnvelope)}
              containerWidth={containerRef.current.clientWidth}
              containerHeight={height * 2}
              duration={maxDuration}
              trackOffset={height}
              transitionStart={transitionStart}
              transitionEnd={transitionEnd}
              enabled={false}
              showPoints={false}
              showCurve={true}
              verticalShift={-0.04}
            />
            
            {/* EQ High Envelope - 浅黄色，向下偏移 */}
            <AutomationEnvelopeOverlay
              trackIndex={1}
              trackColor="rgba(253, 224, 71, 0.8)"
              envelope={track2EQHighEnvelope}
              onEnvelopeChange={(newEnvelope) => setTrack2EQHighEnvelope(newEnvelope)}
              containerWidth={containerRef.current.clientWidth}
              containerHeight={height * 2}
              duration={maxDuration}
              trackOffset={height}
              transitionStart={transitionStart}
              transitionEnd={transitionEnd}
              enabled={false}
              showPoints={false}
              showCurve={true}
              verticalShift={-0.04}
            />
          </>
        )}
      </div>
      
      {/* 快捷键提示面板 */}
      <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 dark:from-blue-900/10 dark:via-purple-900/10 dark:to-pink-900/10 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">⌨️ 快捷键</span>
            <span className="text-xs text-gray-600 dark:text-gray-400">(Keyboard Shortcuts)</span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="flex items-center gap-2 bg-white/60 dark:bg-gray-800/40 rounded px-2 py-1">
            <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded font-mono font-semibold">Space</kbd>
            <span className="text-gray-700 dark:text-gray-300">播放/暂停</span>
          </div>
          
          <div className="flex items-center gap-2 bg-white/60 dark:bg-gray-800/40 rounded px-2 py-1">
            <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded font-mono font-semibold">A</kbd>
            <span className="text-gray-700 dark:text-gray-300">后退5秒</span>
          </div>
          
          <div className="flex items-center gap-2 bg-white/60 dark:bg-gray-800/40 rounded px-2 py-1">
            <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded font-mono font-semibold">D</kbd>
            <span className="text-gray-700 dark:text-gray-300">前进5秒</span>
          </div>
          
          <div className="flex items-center gap-2 bg-white/60 dark:bg-gray-800/40 rounded px-2 py-1">
            <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded font-mono font-semibold">←</kbd>
            <span className="text-gray-700 dark:text-gray-300">转场左移4拍</span>
          </div>
          
          <div className="flex items-center gap-2 bg-white/60 dark:bg-gray-800/40 rounded px-2 py-1">
            <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded font-mono font-semibold">→</kbd>
            <span className="text-gray-700 dark:text-gray-300">转场右移4拍</span>
          </div>
          
          <div className="flex items-center gap-2 bg-white/60 dark:bg-gray-800/40 rounded px-2 py-1">
            <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded font-mono font-semibold">1</kbd>
            <span className="text-gray-700 dark:text-gray-300">静音T1</span>
          </div>
          
          <div className="flex items-center gap-2 bg-white/60 dark:bg-gray-800/40 rounded px-2 py-1">
            <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded font-mono font-semibold">2</kbd>
            <span className="text-gray-700 dark:text-gray-300">静音T2</span>
          </div>
          
          <div className="flex items-center gap-2 bg-white/60 dark:bg-gray-800/40 rounded px-2 py-1">
            <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded font-mono font-semibold">3</kbd>
            <span className="text-gray-700 dark:text-gray-300">取消静音</span>
          </div>
        </div>
      </div>
      
      {/* Automation 控制
      <div className="mt-3 p-3 bg-gradient-to-r from-purple-50 via-blue-50 to-yellow-50 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-yellow-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              📈 完整自动化系统
            </span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              automationEnabled
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}>
              {automationEnabled ? '✅ 已启用' : '⏸️ 已暂停'}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutomationEnabled(!automationEnabled)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                automationEnabled
                  ? 'bg-orange-500 hover:bg-orange-600 text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
            >
              {automationEnabled ? '⏸️ 暂停' : '▶️ 启用'}
            </button>
            
          </div>
        </div>

        
        <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
          <p>
            💡 <strong>使用说明：</strong>自动化系统控制转场区域的 Filter、Volume 和 EQ 效果。
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-purple-500"></div>
              <span><strong>紫色曲线</strong> = Filter</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-blue-500"></div>
              <span><strong>蓝色曲线</strong> = Volume</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-yellow-600"></div>
              <span><strong>深黄曲线</strong> = EQ Low</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-yellow-400"></div>
              <span><strong>中黄曲线</strong> = EQ Mid</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-yellow-200"></div>
              <span><strong>浅黄曲线</strong> = EQ High</span>
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            📍 <strong>提示：</strong>点击波形可以直接 seek 播放位置，Automation 曲线不会阻挡操作。
          </p>
          <p className="text-amber-600 dark:text-amber-400">
            ⚡ <strong>技术：</strong>Filter 使用 Moog 算法，Volume 和 EQ 实时控制 Tone.js 效果链。
          </p>
        </div>
      </div> */}
      
    </div>
  )
}

